import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/WhatsAppChat.css';

const WhatsAppChat = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [pulseAnimation, setPulseAnimation] = useState(true);

  // WhatsApp number (same as in contact form)
  const whatsappNumber = "919243015433";
  
  // Pre-defined message for better user experience
  const defaultMessage = "Hi! I'm interested in your services. Can we discuss my project requirements?";

  useEffect(() => {
    // Show button after a short delay for better UX
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 2000);

    // Stop pulse animation after user interaction or after some time
    const pulseTimer = setTimeout(() => {
      setPulseAnimation(false);
    }, 10000);

    return () => {
      clearTimeout(timer);
      clearTimeout(pulseTimer);
    };
  }, []);

  const handleWhatsAppClick = () => {
    const encodedMessage = encodeURIComponent(defaultMessage);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    // Stop pulse animation after first click
    setPulseAnimation(false);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    setPulseAnimation(false); // Stop pulse when user hovers
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Mobile touch interactions
  const handleTouchStart = () => {
    setIsHovered(true);
    setPulseAnimation(false);
  };

  const handleTouchEnd = () => {
    // Keep tooltip visible for a moment on mobile
    setTimeout(() => {
      setIsHovered(false);
    }, 1500);
  };

  if (!isVisible) return null;

  return (
    <div className="whatsapp-chat-container">
      <motion.div
        className={`whatsapp-chat-button ${pulseAnimation ? 'pulse' : ''}`}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ 
          type: "spring", 
          stiffness: 260, 
          damping: 20,
          delay: 0.2 
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleWhatsAppClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* WhatsApp Icon */}
        <div className="whatsapp-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M17.472 14.382C17.185 14.239 15.726 13.526 15.462 13.429C15.198 13.332 15.006 13.284 14.814 13.571C14.622 13.858 14.049 14.526 13.881 14.718C13.713 14.91 13.545 14.934 13.258 14.791C12.971 14.648 12.004 14.333 10.851 13.296C9.957 12.494 9.345 11.516 9.177 11.229C9.009 10.942 9.161 10.784 9.304 10.641C9.433 10.512 9.591 10.302 9.734 10.134C9.877 9.966 9.925 9.846 10.022 9.654C10.119 9.462 10.071 9.294 10 9.151C9.929 9.008 9.345 7.549 9.105 6.975C8.872 6.418 8.634 6.491 8.458 6.482C8.29 6.473 8.098 6.471 7.906 6.471C7.714 6.471 7.403 6.542 7.139 6.829C6.875 7.116 6.114 7.829 6.114 9.288C6.114 10.747 7.163 12.158 7.306 12.35C7.449 12.542 9.343 15.492 12.305 16.784C13.04 17.112 13.615 17.311 14.064 17.453C14.804 17.693 15.481 17.658 16.017 17.585C16.616 17.505 17.797 16.859 18.037 16.154C18.277 15.449 18.277 14.839 18.206 14.718C18.135 14.597 17.759 14.478 17.472 14.382Z"
              fill="currentColor"
            />
            <path
              d="M20.5 3.5C18.25 1.25 15.25 0 12 0C5.463 0 0.162 5.301 0.162 11.838C0.162 13.938 0.713 15.988 1.75 17.8L0 24L6.338 22.288C8.088 23.238 10.038 23.738 12.025 23.738C18.562 23.738 23.863 18.437 23.863 11.9C23.863 8.7 22.75 5.75 20.5 3.5ZM12 21.738C10.2 21.738 8.45 21.25 6.913 20.325L6.55 20.113L2.825 21.088L3.813 17.463L3.575 17.088C2.563 15.5 2.025 13.7 2.025 11.838C2.025 6.325 6.488 1.863 12 1.863C14.688 1.863 17.2 2.913 19.075 4.787C20.95 6.662 22 9.175 22 11.863C22 17.375 17.513 21.738 12 21.738Z"
              fill="currentColor"
            />
          </svg>
        </div>

        {/* Hover Tooltip */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              className="whatsapp-tooltip"
              initial={{ opacity: 0, x: 20, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="tooltip-content">
                <div className="tooltip-title">Chat with our Professional Team</div>
                <div className="tooltip-subtitle">Get instant support & consultation</div>
              </div>
              <div className="tooltip-arrow"></div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pulse rings for attention */}
        {pulseAnimation && (
          <>
            <div className="pulse-ring pulse-ring-1"></div>
            <div className="pulse-ring pulse-ring-2"></div>
            <div className="pulse-ring pulse-ring-3"></div>
          </>
        )}

        {/* Online indicator */}
        <div className="online-indicator">
          <div className="online-dot"></div>
        </div>
      </motion.div>

      {/* Floating notification (optional - shows briefly) */}
      <AnimatePresence>
        {isVisible && pulseAnimation && (
          <motion.div
            className="whatsapp-notification"
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ delay: 3, duration: 0.5 }}
          >
            <div className="notification-content">
              <div className="notification-avatar">
                <img src="/assets/images/LOGO.png" alt="Support" />
              </div>
              <div className="notification-text">
                <div className="notification-title">Need help?</div>
                <div className="notification-message">Chat with us on WhatsApp!</div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WhatsAppChat;
